import { supabase } from '@/integrations/supabase/client';
import { getDetailedGroupMembers } from '../user-groups';
import type { SendNotificationRequest, SendNotificationResponse, SendProgress } from '@/types/notifications';

// Render template with variables
export function renderTemplate(template: string, variables: Record<string, unknown>): string {
  let rendered = template;

  Object.entries(variables).forEach(([key, value]) => {
    const placeholder = new RegExp(`{{${key}}}`, 'g');
    rendered = rendered.replace(placeholder, String(value));
  });

  return rendered;
}

// Helper function to create delivery attempts for a notification delivery
export const createDeliveryAttempts = async (deliveryId: number, userId: number) => {
  try {
    const { data: pushTokens, error: tokensError } = await supabase
      .from('user_push_tokens')
      .select('id, platform, device_model, app_version, os_version')
      .eq('user_id', userId)
      .eq('is_active', true);

    if (tokensError) {
      console.error('Error fetching push tokens:', tokensError);
      return { success: false, error: tokensError.message };
    }

    if (!pushTokens || pushTokens.length === 0) {
      console.warn(`No active push tokens found for user ${userId}`);
      return { success: true, attemptsCreated: 0 };
    }

    const attempts = pushTokens.map(token => ({
      delivery_id: deliveryId,
      push_token_id: token.id,
      platform: token.platform,
      device_model: token.device_model,
      app_version: token.app_version,
      os_version: token.os_version,
      status: 'pending',
      user_id: userId,
    }));

    const { error: attemptsError } = await supabase
      .from('notification_delivery_attempts')
      .insert(attempts);

    if (attemptsError) {
      console.error('Error creating delivery attempts:', attemptsError);
      return { success: false, error: attemptsError.message };
    }

    return { success: true, attemptsCreated: attempts.length };
  } catch (error) {
    console.error('Error in createDeliveryAttempts:', error);
    return { success: false, error: (error as Error).message };
  }
};

// Send immediate notification to specific users
export const sendImmediateNotification = async (
  request: SendNotificationRequest,
  onProgress?: (progress: SendProgress) => void
): Promise<SendNotificationResponse> => {
  try {
    // Get template details (optional — if template_id is provided)
    let template: {
      id: number;
      title_template: string;
      body_template: string;
      action_url: string | null;
      image_url: string | null;
    } | null = null;

    if (request.template_id) {
      const { data: templateData, error: templateError } = await supabase
        .from('notification_templates')
        .select('*')
        .eq('id', request.template_id)
        .single();

      if (templateError || !templateData) {
        throw new Error('Template not found');
      }
      template = templateData;
    } else if (!request.title || !request.body) {
      throw new Error('Either template_id or both title and body must be provided');
    }

    // Resolve title/body — direct values take priority, then template
    const baseTitleTemplate = request.title || template!.title_template;
    const baseBodyTemplate = request.body || template!.body_template;
    const baseActionUrl = template?.action_url || null;
    const baseImageUrl = request.image_url || template?.image_url || null;

    // Get target users
    let targetUsers: Array<{ id: number; name: string; email: string }> = [];

    if (request.topic) {
      const userVariables = {
        ...request.variables,
        name: 'User',
      }
      const { data: deliveryData, error: deliveryError } = await supabase
        .from('notification_deliveries')
        .insert({
          template_id: template?.id || null,
          image_url: baseImageUrl,
        })
        .select('id').single();
      if (deliveryError) throw deliveryError;

      const mappingData = {
        delivery_id: deliveryData.id,
        topic: request.topic,
        rendered_title: renderTemplate(baseTitleTemplate, userVariables),
        rendered_body: renderTemplate(baseBodyTemplate, userVariables),
        action_url: baseActionUrl ? renderTemplate(baseActionUrl, userVariables) : null,
        status: 'sent',
      }
      const { error: mappingError } = await supabase.from('notification_delivery_mapping').insert(mappingData);
      if (mappingError) throw mappingError;
      const { data: sendResult, error: sendError } = await supabase.functions.invoke(
        'send-notification',
        {
          body: {
            topic: mappingData.topic,
            noti_title: mappingData.rendered_title,
            noti_body: mappingData.rendered_body,
            noti_action_url: mappingData.action_url,
          },
        }
      );

      if (sendError) throw sendError;

      return {
        success: sendResult.success,
        message: sendResult.message,
      };
    } else if (request.user_ids && request.user_ids.length > 0) {
      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('id, name, email')
        .in('id', request.user_ids)
        .eq('is_active', true);

      if (usersError) throw usersError;
      targetUsers = users || [];
    } else if (request.user_group_id) {
      try {
        console.log(`Getting members for user group ${request.user_group_id}`);

        const PAGE_SIZE = 1000;
        let allUsers: Array<{
          user_id: number;
          name: string;
          email: string;
          avatar_url: string;
          created_at: string;
          subscription_status: string;
          subscription_plan_name: string;
        }> = [];
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
          const users = await getDetailedGroupMembers(request.user_group_id, PAGE_SIZE, offset);
          allUsers = [...allUsers, ...users];

          hasMore = users.length === PAGE_SIZE;
          offset += PAGE_SIZE;

          if (offset > 50000) {
            console.warn('Reached maximum offset limit, stopping pagination');
            break;
          }
        }

        console.log(`Found ${allUsers.length} members in user group`);
        targetUsers = allUsers.map(user => ({
          id: user.user_id,
          name: user.name,
          email: user.email
        }));
        console.log(`Mapped ${targetUsers.length} target users for notification`);
      } catch (usersError) {
        console.error('Error getting group members:', usersError);
        throw new Error(`Failed to get group members: ${(usersError as Error).message}`);
      }
    } else {
      throw new Error('Either user_ids or user_group_id must be provided');
    }

    if (targetUsers.length === 0) {
      return {
        success: true,
        message: 'No target users found',
        delivery_ids: [],
      };
    }

    // Create notification delivery header
    const errors: string[] = [];

    const BATCH_SIZE = targetUsers.length > 1000 ? 100 : 50;
    const totalBatches = Math.ceil(targetUsers.length / BATCH_SIZE);

    console.log(`Processing ${targetUsers.length} users in ${totalBatches} batches of ${BATCH_SIZE}`);

    onProgress?.({
      current: 0,
      total: targetUsers.length,
      message: `Starting to process ${targetUsers.length} users in ${totalBatches} batches...`
    });

    const { data: deliveryData, error: batchError } = await supabase
      .from('notification_deliveries')
      .insert({
        template_id: template?.id || null,
        image_url: baseImageUrl,
      })
      .select('id').single();

    if (batchError || !deliveryData) {
      throw new Error(`Failed to create notification delivery: ${batchError?.message || 'No data returned'}`);
    }

    const deliveryMappings = targetUsers.map(user => {
      const userVariables = {
        ...request.variables,
        name: user.name,
      };

      return {
        delivery_id: deliveryData.id,
        user_id: user.id,
        rendered_title: renderTemplate(baseTitleTemplate, userVariables),
        rendered_body: renderTemplate(baseBodyTemplate, userVariables),
        action_url: baseActionUrl ? renderTemplate(baseActionUrl, userVariables) : null,
        status: 'pending'
      };
    });

    // Insert all mappings first (before attempts, to avoid race condition)
    const mappingChunks: typeof deliveryMappings[] = [];
    for (let i = 0; i < deliveryMappings.length; i += BATCH_SIZE) {
      mappingChunks.push(deliveryMappings.slice(i, i + BATCH_SIZE));
    }

    for (let i = 0; i < mappingChunks.length; i++) {
      console.log(`[sendImmediate] Inserting mapping batch ${i + 1}/${mappingChunks.length}:`,
        mappingChunks[i].map(m => ({ delivery_id: m.delivery_id, user_id: m.user_id, title: m.rendered_title?.slice(0, 30) })));
      const { error: mappingError } = await supabase
        .from('notification_delivery_mapping')
        .insert(mappingChunks[i]);

      if (mappingError) {
        console.error(`[sendImmediate] Mapping batch ${i + 1} FAILED:`, mappingError);
        errors.push(`Mapping batch ${i + 1}: ${mappingError.message}`);
      } else {
        console.log(`[sendImmediate] Mapping batch ${i + 1} OK`);
      }

      onProgress?.({
        current: Math.min((i + 1) * BATCH_SIZE, targetUsers.length),
        total: targetUsers.length,
        message: `Mapped ${Math.min((i + 1) * BATCH_SIZE, targetUsers.length)}/${targetUsers.length} users...`
      });
    }

    // Create delivery attempts after mappings are committed
    onProgress?.({
      current: targetUsers.length,
      total: targetUsers.length,
      message: `Creating delivery attempts for ${targetUsers.length} users...`
    });

    const attemptResults = await Promise.all(
      targetUsers.map(user => createDeliveryAttempts(deliveryData.id, user.id))
    );

    attemptResults.forEach((result, index) => {
      if (!result.success) {
        errors.push(`Delivery attempt for user ${targetUsers[index].id}: ${result.error}`);
      }
    });

    onProgress?.({
      current: targetUsers.length,
      total: targetUsers.length,
      message: `Sending notifications...`
    });

    // Send notifications immediately
    const { data: sendResult, error: sendError } = await supabase.functions.invoke(
      'send-notification',
      {
        body: { delivery_ids: [deliveryData.id] },
      }
    );

    if (sendError) throw sendError;

    let responseMessage = sendResult.message;
    if (errors.length > 0) {
      responseMessage += ` (Some errors occurred: ${errors.join('; ')})`;
    }

    return {
      success: sendResult.success,
      message: responseMessage,
      delivery_ids: [deliveryData.id],
    };

  } catch (error) {
    console.error('Error sending immediate notification:', error);
    throw error;
  }
};
